import React, { PropsWithChildren, FC, useCallback, useMemo, useState } from "react";

import {
    Accordion,
    Button,
    FieldCheckbox,
    LoadingSpinner,
    URLInput,
    validateURL
} from "@xapp/components";

export interface WebsiteQuestionStepProps {
    /**
     * Optional style applied to the root div
     */
    style?: React.CSSProperties;
    /**
     * Optional class name to apply to the root div
     */
    className?: string;
    /**
     * Has analysis started
     */
    analyzing?: boolean;
    /**
     * Status message to display
     */
    analyzingStatusMessage?: string;
    /**
     * Error message to display to the user if something goes wrong.
     */
    error?: { error: string, message: string };
    /**
     * Initial value to use for the website
     */
    website?: string;
    /**
     * The user has clicked next
     * @param hasWebsite
     * @param website
     */
    onNext(hasWebsite: boolean, website?: string): void;
    /**
     * Optional, user clicks back button
     */
    onBack?(): void;
}

export const WebsiteQuestionStep: FC<PropsWithChildren<WebsiteQuestionStepProps>> = props => {

    const { style, className, onNext, analyzing } = props;

    const [website, setWebsite] = useState<string>(props.website || "");
    const [valid, setValid] = useState<boolean>(validateURL(props.website).valid);

    const [hasWebsite, setHasWebsite] = useState<boolean>();

    const handleWebsiteChange = useCallback((value: string, valid: boolean) => {
        setWebsite(value);
        setValid(valid);
    }, []);

    const handleNextClick = () => {
        const returnWebsite = hasWebsite ? website : undefined;
        onNext(hasWebsite, returnWebsite);
    };

    const handleHasWebsiteChange = useCallback((value: boolean) => {
        setHasWebsite(value);
    }, []);

    const handleNoWebsiteChange = useCallback((value: boolean) => {
        setHasWebsite(!value);
    }, []);

    const noWebsite: boolean = useMemo(() => {
        return typeof hasWebsite === "boolean" ? !hasWebsite : false;
    }, [hasWebsite])

    if (analyzing) {
        return (
            <div style={{ ...style }} className={className}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
                    <LoadingSpinner />
                    <h2>{props.analyzingStatusMessage}</h2>
                </div>
            </div>
        );
    }

    const canClickNext: boolean = hasWebsite ? (website && valid) : noWebsite;

    return (
        <div style={{ ...style }} className={className}>
            <div>
                <h2>Do you have a website?</h2>
                <p>If you have a website, we can use it's content to help train your AI, accelerate the onboarding process by importing your brand, and provide specific instructions on how to install the widgets on your site.</p>
                <div style={{ padding: "20px 0px 50px 0px" }}>
                    <Accordion
                        title={(
                            <div style={{ display: "flex", flexDirection: "row", alignContent: "center", alignItems: "center" }}>
                                <FieldCheckbox
                                    fieldStyle={{ marginBottom: "2px" }}
                                    onChange={handleHasWebsiteChange}
                                    value={typeof hasWebsite === "boolean" ? hasWebsite : false}
                                />
                                <h3>I have a website (recommended)</h3>
                            </div>
                        )} onChange={(event, expanded) => {
                            // event.preventDefault();
                            handleHasWebsiteChange(expanded)
                        }}
                        disableExpandIcon={true}
                        expanded={typeof hasWebsite === "boolean" ? hasWebsite : false}>
                        <div>
                            <div style={{ paddingBottom: "20px" }}><p>Great! Please enter it in below...</p></div>
                            <URLInput
                                label="Business Website"
                                onChange={handleWebsiteChange}
                                disabled={typeof hasWebsite === "boolean" ? !hasWebsite : false}
                                value={website} />
                        </div>
                    </Accordion>
                    <Accordion
                        title={(
                            <div style={{ display: "flex", flexDirection: "row", alignContent: "center", alignItems: "center" }}>
                                <FieldCheckbox
                                    fieldStyle={{ marginBottom: "2px" }}
                                    onChange={handleNoWebsiteChange}
                                    value={noWebsite}
                                />
                                <h3>I don't have a website</h3>
                            </div>
                        )} onChange={(event, expanded) => {
                            // event.preventDefault();
                            handleNoWebsiteChange(expanded)
                        }}
                        disableExpandIcon={true}
                        expanded={typeof noWebsite === "boolean" ? noWebsite : false}>
                        <div style={{ paddingBottom: "20px" }}>
                            <p>No problem, you can always add one later.  We will focus on setting up Reserve with Google & Book Online </p>
                        </div>
                    </Accordion>
                </div>
                <div style={{ padding: "20px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "right" }}>
                    <Button
                        rightIcon="arrow_forward"
                        label={`Next`}
                        variation="primary"
                        onClick={handleNextClick}
                        disabled={!canClickNext}
                    />
                </div>
            </div >
        </div>
    );
}